import type { CSSProperties } from 'react'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Flex, Text, Typography, type TypographyVariant } from '@percent/lemonade'
import type { ProductOfferCtaCard } from '@percent/cause-dashboard/storyblok/types/storyblok-components'
import { useNativeApply } from '@percent/cause-dashboard/common/components/nativeApply'
import { useIsOrganisationInactive, useMarketplaceAnalytics } from '@percent/cause-dashboard/common/hooks'
import {
  getResolvedNativeApplyContext,
  type NativeApplyModalContext
} from '@percent/cause-dashboard/constants/nativeApply/getResolvedNativeApplyContext'
import { resolveNativeApplyPartnerKey } from '@percent/cause-dashboard/constants/nativeApply/resolveNativeApplyPartner'
import type { SmartApplyPartnerConfigKey } from '@percent/cause-dashboard/constants/smartApplyPartners'
import {
  MarketplaceProgrammeVisitedSource,
  OriginCategory
} from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/marketplaceAnalytics.types'
import { RichTextRenderer } from '../richTextRenderer/RichTextRenderer'
import styles from './ProductOfferCTACard.module.scss'

export type ProductOfferCTACardCMSProps = {
  card: ProductOfferCtaCard
}

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href.trim())
}

type ResolvedCardCta =
  | { kind: 'link'; href: string }
  | {
      kind: 'native_apply'
      partnerKey: SmartApplyPartnerConfigKey
      modalContext: NativeApplyModalContext
      /** Fallback link opened instead of the modal when the organisation is not active. */
      fallbackHref: string
    }

function resolveCardCta(card: ProductOfferCtaCard): ResolvedCardCta | null {
  if (card.cta_type === 'native_apply') {
    const partnerKey = resolveNativeApplyPartnerKey(card.native_apply_partner)
    const modalContext = getResolvedNativeApplyContext(card.offer)
    if (!partnerKey || !modalContext) {
      return null
    }
    return { kind: 'native_apply', partnerKey, modalContext, fallbackHref: card.cta_link?.trim() ?? '' }
  }

  const href = card.cta_link?.trim() ?? ''
  if (!href) {
    return null
  }
  return { kind: 'link', href }
}

export function ProductOfferCTACard({ card }: Readonly<ProductOfferCTACardCMSProps>) {
  const navigate = useNavigate()
  const { openNativeApply } = useNativeApply()
  const { trackProgramVisited } = useMarketplaceAnalytics()
  const isOrganisationInactive = useIsOrganisationInactive()

  const resolvedCta = useMemo(() => resolveCardCta(card), [card])
  const buttonText = card.button_text?.trim() ?? ''

  const openHref = useCallback(
    (href: string) => {
      if (isExternalHref(href)) {
        window.open(href, '_blank', 'noopener,noreferrer')
      } else {
        navigate(href.startsWith('/') ? href : `/${href}`)
        window.scrollTo(0, 0)
      }
    },
    [navigate]
  )

  const onButtonClick = useCallback(() => {
    if (!resolvedCta) {
      return
    }

    if (resolvedCta.kind === 'native_apply') {
      // Inactive orgs fall back to opening the link instead of the modal (see below), so the click
      // is only a native apply when the modal will actually open.
      const willOpenNativeModal = !isOrganisationInactive
      trackProgramVisited({
        source: MarketplaceProgrammeVisitedSource.ProductPage,
        targetUrl: willOpenNativeModal ? '' : resolvedCta.fallbackHref,
        isNativeApply: willOpenNativeModal,
        programmeOfferName: resolvedCta.modalContext.title,
        originCategory: OriginCategory.ProductOfferDetail
      })

      // When the organisation is not active the native apply submission would fail, so fall
      // back to opening the configured link instead of the modal.
      if (isOrganisationInactive) {
        openHref(resolvedCta.fallbackHref)
        return
      }

      openNativeApply({
        partnerKey: resolvedCta.partnerKey,
        modalContext: resolvedCta.modalContext,
        tracking: {
          programmeOfferName: resolvedCta.modalContext.title,
          source: 'product-offer-cta-card',
          originCategory: OriginCategory.ProductOfferDetail
        }
      })
      return
    }

    trackProgramVisited({
      source: MarketplaceProgrammeVisitedSource.ProductPage,
      targetUrl: resolvedCta.href,
      isNativeApply: false,
      programmeOfferName: card.title,
      originCategory: OriginCategory.ProductOfferDetail
    })

    openHref(resolvedCta.href)
  }, [resolvedCta, openNativeApply, openHref, isOrganisationInactive, trackProgramVisited, card.title])

  const hasBadge = Boolean(card.badge_text?.trim())
  const subtitleVariant: TypographyVariant = card.subtitleVariant ?? 'default'
  const bottomInfo = card.bottom_info?.trim() ?? ''
  const hasBottomInfo = bottomInfo.length > 0
  const showButton = Boolean(resolvedCta) && buttonText.length > 0

  return (
    <div className={styles.cardFlex}>
      <div
        className={`${styles.productOfferCTACardWrapper} ${styles.cardOuterInGroup}`}
        style={
          {
            '--product-offer-cta-card-padding': '0px',
            '--product-offer-cta-card-button-margin-top': '24px',
            width: '100%'
          } as CSSProperties
        }
      >
        <div className={hasBadge ? styles.featuredCardWrapper : styles.cardWrapper}>
          {hasBadge && (
            <div className={styles.badgeTextWrapper}>
              <Text size="large" fontWeight="semiBold" color="#8B4AFF">
                {card.badge_text}
              </Text>
            </div>
          )}
          <Flex direction="column" className={styles.cardContent}>
            <Typography variant="h3">{card.title}</Typography>
            {card.subtitle ? (
              <div className={styles.subtitleWrapper}>
                <Typography variant={subtitleVariant}>{card.subtitle}</Typography>
              </div>
            ) : null}

            {card.content ? (
              <div className={styles.featuresWrapper}>
                <RichTextRenderer document={card.content} variant="nestedInCard" />
              </div>
            ) : null}

            {showButton ? (
              <Box
                className={styles.buttonWrapper}
                style={{ '--product-offer-cta-card-button-margin-top': '24px' } as CSSProperties}
              >
                <Button variant="primary" onClick={onButtonClick}>
                  {buttonText}
                </Button>
              </Box>
            ) : null}
            {hasBottomInfo ? (
              <div className={styles.bottomInfoWrapper}>
                <Typography variant="default">{bottomInfo}</Typography>
              </div>
            ) : null}
          </Flex>
        </div>
      </div>
    </div>
  )
}
